import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import {
  XMarkIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { submitFeedback, type FeedbackCategory, type FeedbackSeverity } from '@/api/feedback';
import { extractError, type NormalizedError } from '@/api/errors';
import { captureScreen } from './screenCapture';
import { Toast } from './Toast';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  severity?: FeedbackSeverity;
  what_happened: string;
  expected: string;
  actual: string;
  body: string;
}

const CATEGORIES: { value: FeedbackCategory; label: string; description: string }[] = [
  { value: 'bug', label: 'Bug', description: "Something's broken or behaving wrong." },
  { value: 'idea', label: 'Idea', description: 'A feature or improvement you want.' },
  { value: 'praise', label: 'Praise', description: 'Something that worked great.' },
  { value: 'other', label: 'Other', description: 'Anything else.' },
];

const SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'blocker', label: 'Blocker' },
];

interface ToastState {
  show: boolean;
  variant: 'success' | 'error';
  message: string;
}

type SubmitState = 'idle' | 'submitting' | 'error' | 'success';

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState<'snapshot' | null>(null);
  // Submit lifecycle: idle → submitting → (error → idle | success → close).
  // The legacy `submitting` boolean became unwieldy when we added in-modal
  // success/error states that shouldn't toast.
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState<NormalizedError | null>(null);
  const [trackingId, setTrackingId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>({ show: false, variant: 'success', message: '' });
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const submitting = submitState === 'submitting';

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      severity: 'med',
      what_happened: '',
      expected: '',
      actual: '',
      body: '',
    },
  });

  // Generate a stable idempotency key when the modal opens; reset state on close.
  useEffect(() => {
    if (open) {
      const key =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setIdempotencyKey(key);
      setSubmitState('idle');
      setSubmitError(null);
      setTrackingId(null);
      setCategoryError(null);
    } else {
      // Defer a tick so closing animation doesn't flicker.
      setStep(1);
      setCategory(null);
      setCategoryError(null);
      clearScreenshot();
      reset();
      setSubmitState('idle');
      setSubmitError(null);
      setTrackingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Preview URL lifecycle.
  useEffect(() => {
    if (!screenshot) {
      setScreenshotUrl(null);
      return;
    }
    const url = URL.createObjectURL(screenshot);
    setScreenshotUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  // Clipboard paste on step 3.
  useEffect(() => {
    if (!open || step !== 3) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            setScreenshot(file);
            e.preventDefault();
            return;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, step]);

  const clearScreenshot = () => {
    setScreenshot(null);
  };

  const runCapture = async (method: 'snapshot') => {
    setCapturing(method);
    try {
      const blob = await captureScreen(method);
      if (blob) {
        setScreenshot(blob);
      } else {
        setToast({
          show: true,
          variant: 'error',
          message: 'Page snapshot failed — try uploading an image instead.',
        });
      }
    } finally {
      setCapturing(null);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setScreenshot(file);
    }
    // reset to allow re-selecting the same file
    e.target.value = '';
  };

  // Step 1's Next button stays enabled — clicking without a category set
  // surfaces an inline helper rather than silently dead-ending. Premium
  // touch: never disable a control without telling the user why.
  const nextDisabled = useMemo(() => false, []);

  const goNext = () => {
    if (step === 1) {
      if (!category) {
        setCategoryError('Pick a category so we can route this to the right place.');
        return;
      }
      setCategoryError(null);
      setStep(2);
    } else if (step === 2) setStep(3);
  };

  const goBack = () => {
    if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  };

  // IMPORTANT: do NOT bind this to a form's onSubmit. Historically the
  // wrapping <form>'s submit event was firing from step 2 (Enter in a
  // textarea, autofill, or a conditionally-rendered submit button getting
  // a stale click), skipping the screenshot step. The Send Feedback
  // button now calls this directly via onClick.
  // Build the FormData payload — split out so the Retry button can re-call
  // the same submit logic without re-rendering the form fields.
  const buildFormData = (values: FormValues): FormData => {
    const formData = new FormData();
    if (category) formData.append('category', category);
    if (category === 'bug' && values.severity) {
      formData.append('severity', values.severity);
    }
    formData.append('what_happened', values.what_happened || values.body || '');
    if (category === 'bug') {
      formData.append('expected', values.expected || '');
      formData.append('actual', values.actual || '');
    } else {
      formData.append('body', values.body || '');
    }
    formData.append('url', window.location.href);
    formData.append('route', window.location.pathname);
    formData.append(
      'browser_meta',
      JSON.stringify({
        ua: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }),
    );
    if (screenshot) {
      const filename = screenshot instanceof File ? screenshot.name : 'screenshot.png';
      formData.append('screenshot', screenshot, filename);
    }
    return formData;
  };

  const doSubmit = handleSubmit(async (values) => {
    if (!category || step !== 3) return;
    setSubmitState('submitting');
    setSubmitError(null);
    try {
      const resp = await submitFeedback(buildFormData(values), idempotencyKey);
      setTrackingId(resp?.id ?? null);
      setSubmitState('success');
      // Auto-close after the SuccessCard's reveal — long enough for the
      // user to read the tracking ID, short enough to feel snappy.
      window.setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      const norm = extractError(err);
      // 422 → field-level errors render inline next to the offending field
      // (no toast). Jump back to the relevant step if needed.
      if (norm.code === 'validation' && Object.keys(norm.fieldErrors).length > 0) {
        const step2Fields = ['what_happened', 'expected', 'actual', 'body', 'severity'];
        const hasStep2 = step2Fields.some((k) => k in norm.fieldErrors);
        for (const [key, msg] of Object.entries(norm.fieldErrors)) {
          // Only set errors on fields the form knows about — react-hook-form
          // treats unknown field names as a hard error.
          if (
            key === 'what_happened' ||
            key === 'expected' ||
            key === 'actual' ||
            key === 'body' ||
            key === 'severity'
          ) {
            setError(key, { message: msg, type: 'server' });
          }
        }
        // Submit only fires from step 3, but the field-error may belong to
        // step 2 (severity, what_happened, etc.) — bounce back so the
        // operator sees the underlined field, not a silent failure.
        if (hasStep2) setStep(2);
        setSubmitState('idle');
      } else {
        // Network / server / rate-limit errors: bordered card inside the
        // modal (not a toast). Modal stays open, Retry available.
        setSubmitError(norm);
        setSubmitState('error');
      }
    }
  });

  const retrySubmit = () => {
    setSubmitError(null);
    void doSubmit();
  };

  // Theme-aware via CSS vars (see frontend/src/theme.css). Light-mode work
  // is out of scope for this PR but using vars here means the modal stops
  // bleeding dark colors over a future light theme — no future bandaid.
  const inputClass =
    'mt-1 block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/30 ' +
    'border-[var(--dv-border-default)] bg-[var(--dv-bg-input)] text-[var(--dv-text-primary)] placeholder:text-[var(--dv-text-muted)] focus:border-cyan-500/60';
  const labelClass = 'block text-xs font-medium tracking-wider uppercase text-[var(--dv-text-secondary)]';
  const errorClass = 'mt-1 text-xs text-[var(--dv-status-danger,#f87171)]';

  const watchWhat = watch('what_happened');
  const watchExpected = watch('expected');
  const watchActual = watch('actual');
  const watchBody = watch('body');

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[var(--dv-bg-overlay,rgba(0,0,0,0.6))]" data-feedback-overlay="true" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto" data-feedback-overlay="true">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-2 sm:scale-95"
              >
                <Dialog.Panel
                  data-feedback-overlay="true"
                  aria-busy={submitting}
                  className="relative w-full max-w-xl overflow-hidden rounded-lg border shadow-2xl
                             border-[var(--dv-border-default)] bg-[var(--dv-bg-card-solid)] text-[var(--dv-text-primary)]"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between border-b border-[var(--dv-border-default)] px-6 py-4">
                    <div>
                      <Dialog.Title className="text-sm font-semibold tracking-widest uppercase text-[var(--dv-text-primary)]">
                        Send Feedback
                      </Dialog.Title>
                      <p className="mt-1 text-xs text-[var(--dv-text-secondary)]">
                        Step {step} of 3 · Beta
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-[var(--dv-text-muted)] hover:text-[var(--dv-text-primary)]"
                      aria-label="Close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Intentionally NOT a <form> — avoids any Enter/autofill/submit-button
                      click path triggering submission before step 3. */}
                  <div
                    className="px-6 py-5"
                    onKeyDown={(e) => {
                      // Prevent Enter from doing anything except within textareas.
                      if (
                        e.key === 'Enter' &&
                        (e.target as HTMLElement).tagName !== 'TEXTAREA'
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {/* Success card replaces the body — short-lived (1.2s)
                        confirmation with the tracking ID. Builds trust that
                        the request actually landed. */}
                    {submitState === 'success' && (
                      <div
                        role="status"
                        aria-live="polite"
                        className="flex flex-col items-center gap-3 px-2 py-8 text-center
                                   animate-fade-in"
                      >
                        <CheckCircleIcon className="h-10 w-10 text-cyan-400" aria-hidden="true" />
                        <div>
                          <p className="text-base font-medium text-[var(--dv-text-primary)]">
                            Thanks — we got it.
                          </p>
                          <p className="mt-1 text-xs text-[var(--dv-text-secondary)]">
                            Closing this window for you…
                          </p>
                        </div>
                        {trackingId && (
                          <p className="font-mono text-[10px] tracking-widest text-[var(--dv-text-muted)]">
                            TRACKING ID · {trackingId}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Step 1 — Category */}
                    {submitState !== 'success' && step === 1 && (
                      <div className="space-y-3">
                        <p className="text-sm text-[var(--dv-text-secondary)]">What kind of feedback is this?</p>
                        <div role="radiogroup" aria-label="Feedback category" aria-describedby="category-error" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {CATEGORIES.map((c) => (
                            <label
                              key={c.value}
                              className={clsx(
                                'flex cursor-pointer flex-col rounded-md border p-3 transition-colors',
                                category === c.value
                                  ? 'border-cyan-500/50 bg-[var(--dv-bg-input)]'
                                  : 'border-[var(--dv-border-default)] bg-[var(--dv-bg-input)]/50 hover:border-[var(--dv-border-strong)]',
                              )}
                            >
                              <input
                                type="radio"
                                name="category"
                                value={c.value}
                                className="sr-only"
                                checked={category === c.value}
                                onChange={() => { setCategory(c.value); setCategoryError(null); }}
                              />
                              <span className="text-sm font-medium text-[var(--dv-text-primary)]">{c.label}</span>
                              <span className="text-xs text-[var(--dv-text-secondary)] mt-0.5">{c.description}</span>
                            </label>
                          ))}
                        </div>
                        {categoryError && (
                          <p id="category-error" role="alert" className="text-xs text-red-400">
                            {categoryError}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Step 2 — Details */}
                    {submitState !== 'success' && step === 2 && category === 'bug' && (
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass} htmlFor="severity">
                            Severity
                          </label>
                          <select
                            id="severity"
                            className={inputClass}
                            {...register('severity')}
                          >
                            {SEVERITIES.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass} htmlFor="what_happened">
                            What did you do?
                          </label>
                          <textarea
                            id="what_happened"
                            rows={3}
                            maxLength={5000}
                            className={inputClass}
                            placeholder="e.g. Clicked Predict on site XYZ from the Polygon map"
                            aria-describedby="what_happened-error"
                            aria-invalid={!!errors.what_happened}
                            {...register('what_happened', {
                              required: 'Tell us what was happening (a sentence is enough).',
                              maxLength: { value: 5000, message: 'Keep it under 5000 characters.' },
                            })}
                          />
                          <div className="flex justify-between">
                            <span className={errorClass} id="what_happened-error" role={errors.what_happened ? 'alert' : undefined}>
                              {errors.what_happened?.message}
                            </span>
                            <span className="mt-1 text-[10px] text-[var(--dv-text-muted)]">
                              {(watchWhat || '').length}/5000
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className={labelClass} htmlFor="expected">
                            What did you expect?
                          </label>
                          <textarea
                            id="expected"
                            rows={2}
                            maxLength={2000}
                            className={inputClass}
                            aria-describedby="expected-error"
                            aria-invalid={!!errors.expected}
                            {...register('expected', {
                              required: "Tell us what you expected to happen — even a short note helps.",
                              maxLength: { value: 2000, message: 'Keep it under 2000 characters.' },
                            })}
                          />
                          <div className="flex justify-between">
                            <span className={errorClass} id="expected-error" role={errors.expected ? 'alert' : undefined}>
                              {errors.expected?.message}
                            </span>
                            <span className="mt-1 text-[10px] text-[var(--dv-text-muted)]">
                              {(watchExpected || '').length}/2000
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className={labelClass} htmlFor="actual">
                            What actually happened?
                          </label>
                          <textarea
                            id="actual"
                            rows={2}
                            maxLength={2000}
                            className={inputClass}
                            aria-describedby="actual-error"
                            aria-invalid={!!errors.actual}
                            {...register('actual', {
                              required: "Tell us what actually happened so we can compare.",
                              maxLength: { value: 2000, message: 'Keep it under 2000 characters.' },
                            })}
                          />
                          <div className="flex justify-between">
                            <span className={errorClass} id="actual-error" role={errors.actual ? 'alert' : undefined}>
                              {errors.actual?.message}
                            </span>
                            <span className="mt-1 text-[10px] text-[var(--dv-text-muted)]">
                              {(watchActual || '').length}/2000
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {submitState !== 'success' && step === 2 && category && category !== 'bug' && (
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass} htmlFor="body">
                            Tell us more
                          </label>
                          <textarea
                            id="body"
                            rows={6}
                            maxLength={5000}
                            className={inputClass}
                            placeholder="Share as much context as you'd like."
                            aria-describedby="body-error"
                            aria-invalid={!!errors.body}
                            {...register('body', {
                              required: 'Share a bit of detail so we can act on this.',
                              maxLength: { value: 5000, message: 'Keep it under 5000 characters.' },
                            })}
                          />
                          <div className="flex justify-between">
                            <span className={errorClass} id="body-error" role={errors.body ? 'alert' : undefined}>
                              {errors.body?.message}
                            </span>
                            <span className="mt-1 text-[10px] text-[var(--dv-text-muted)]">
                              {(watchBody || '').length}/5000
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3 — Screenshot */}
                    {submitState !== 'success' && step === 3 && (
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-[var(--dv-text-secondary)]">
                            Add a screenshot (optional).
                          </p>
                          <p className="mt-1 text-xs text-amber-400/90">
                            Your screenshot may be visible to ARGOS admins — avoid capturing
                            sensitive data outside ARGOS.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            disabled={capturing !== null || submitting}
                            onClick={() => runCapture('snapshot')}
                            className="flex flex-col items-center gap-1 rounded-md border px-3 py-3 text-xs
                                       border-[var(--dv-border-default)] bg-[var(--dv-bg-input)]
                                       text-[var(--dv-text-secondary)] hover:border-cyan-500/40 hover:text-[var(--dv-text-primary)]
                                       disabled:opacity-50"
                          >
                            <PhotoIcon className="h-5 w-5 text-[var(--dv-text-secondary)]" />
                            <span>{capturing === 'snapshot' ? 'Capturing…' : 'Use page snapshot'}</span>
                          </button>
                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => fileInputRef.current?.click()}
                            className="flex flex-col items-center gap-1 rounded-md border px-3 py-3 text-xs
                                       border-[var(--dv-border-default)] bg-[var(--dv-bg-input)]
                                       text-[var(--dv-text-secondary)] hover:border-cyan-500/40 hover:text-[var(--dv-text-primary)]
                                       disabled:opacity-50"
                          >
                            <ArrowUpTrayIcon className="h-5 w-5 text-[var(--dv-text-secondary)]" />
                            <span>Upload image</span>
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileInput}
                          />
                        </div>

                        <p className="text-[11px] text-[var(--dv-text-muted)]">
                          You can also paste an image with Ctrl+V.
                        </p>

                        {screenshotUrl && (
                          <div className="relative rounded-md border p-2
                                          border-[var(--dv-border-default)] bg-[var(--dv-bg-input)]">
                            <img
                              src={screenshotUrl}
                              alt="Screenshot preview"
                              className="max-h-48 w-full rounded object-contain"
                            />
                            <button
                              type="button"
                              onClick={clearScreenshot}
                              disabled={submitting}
                              className="absolute top-3 right-3 inline-flex items-center gap-1 rounded
                                         border border-[var(--dv-border-default)]
                                         bg-[var(--dv-bg-card-solid)]/90 px-2 py-1 text-[11px]
                                         text-[var(--dv-text-secondary)] hover:text-red-400 disabled:opacity-50"
                            >
                              <TrashIcon className="h-3 w-3" />
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Inline error banner — bordered red card, modal stays
                        open, Retry button. NOT a toast. Reads role=alert
                        so screen readers announce the failure. */}
                    {submitState === 'error' && submitError && (
                      <div
                        role="alert"
                        aria-live="assertive"
                        className="mt-4 flex items-start gap-3 rounded-md border border-red-500/40
                                   bg-red-500/10 px-3 py-2.5 text-xs text-red-200"
                      >
                        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-300" aria-hidden="true" />
                        <div className="flex-1">
                          <p className="font-medium text-red-100">{submitError.message}</p>
                          {submitError.code === 'rate_limited' && submitError.retryAfterSeconds && (
                            <p className="mt-0.5 text-[11px] text-red-300/80">
                              Retry in ~{Math.max(1, Math.round(submitError.retryAfterSeconds / 60))} minutes.
                            </p>
                          )}
                        </div>
                        {submitError.retryable && (
                          <button
                            type="button"
                            onClick={retrySubmit}
                            className="inline-flex items-center gap-1 rounded border border-red-400/50
                                       px-2 py-1 text-[11px] tracking-widest text-red-100 hover:bg-red-500/20"
                          >
                            <ArrowPathIcon className="h-3 w-3" /> RETRY
                          </button>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    {submitState !== 'success' && (
                      <div className="mt-6 flex items-center justify-between gap-2 border-t border-[var(--dv-border-default)] pt-4">
                        <div>
                          {step > 1 && (
                            <button
                              type="button"
                              onClick={goBack}
                              disabled={submitting}
                              className="text-xs font-medium tracking-wider uppercase text-[var(--dv-text-secondary)] hover:text-[var(--dv-text-primary)] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Back
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="rounded border border-[var(--dv-border-default)] px-3 py-1.5 text-xs font-medium tracking-wider uppercase
                                       text-[var(--dv-text-secondary)] hover:bg-[var(--dv-bg-card-hover)] hover:text-[var(--dv-text-primary)]
                                       disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Cancel
                          </button>
                          {step < 3 ? (
                            <button
                              type="button"
                              onClick={goNext}
                              disabled={nextDisabled}
                              className="rounded bg-cyan-500/20 border border-cyan-500/40 px-3 py-1.5 text-xs font-medium tracking-wider uppercase
                                         text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => doSubmit()}
                              disabled={submitting}
                              className={clsx(
                                'rounded border border-cyan-500/40 px-3 py-1.5 text-xs font-medium tracking-wider uppercase',
                                'text-cyan-200 disabled:cursor-not-allowed',
                                submitting
                                  ? 'bg-[linear-gradient(110deg,rgba(34,211,238,0.18),rgba(34,211,238,0.05),rgba(34,211,238,0.18))] bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none'
                                  : 'bg-cyan-500/20 hover:bg-cyan-500/30',
                              )}
                            >
                              {submitting ? 'Sending…' : 'Send Feedback'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Toast
        show={toast.show}
        variant={toast.variant}
        message={toast.message}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </>
  );
}
